import { useCallback, useLayoutEffect, useRef, type RefObject } from 'react';
import { useRafResizeObserver } from './useRafResizeObserver';

/** Current rendered height of the header — follows the compact animation frame by frame. */
const HEADER_HEIGHT_VAR = '--new-report-header-height';
/** Layout space reserved for the header — the expanded height, held constant while the header is compact. */
const HEADER_OFFSET_VAR = '--new-report-header-offset';

const setPx = (element: HTMLElement, name: string, px: number) => {
  const value = `${px}px`;
  if (element.style.getPropertyValue(name) !== value) {
    element.style.setProperty(name, value);
  }
};

const readPx = (element: HTMLElement, name: string) => parseInt(element.style.getPropertyValue(name), 10) || 0;

const runningAnimations = (element: Element) =>
  typeof element.getAnimations === 'function'
    ? element.getAnimations({ subtree: true }).filter((animation) => animation.playState === 'running')
    : [];

/**
 * Publishes the header height as CSS variables on the scroller so the header can float above the content
 * without ever changing the scrollable height. `HEADER_OFFSET_VAR` only follows the header while it is expanded
 * and its transitions have settled, so collapsing on scroll never moves the content underneath (which would
 * otherwise shorten the page, clamp the scroll position and re-expand the header in a loop). While compact,
 * the offset only grows, in case a resize makes the compact header taller than the reserved space.
 */
export const useFloatingHeaderHeight = (
  headerRef: RefObject<HTMLElement>,
  scrollerRef: RefObject<HTMLElement>,
  isCompact: boolean,
) => {
  const isCompactRef = useRef(isCompact);
  isCompactRef.current = isCompact;
  const waitingForAnimations = useRef(false);

  const measure = useCallback(
    (header: HTMLElement) => {
      const scroller = scrollerRef.current;
      if (!scroller) {
        return;
      }
      const height = Math.ceil(header.getBoundingClientRect().height);
      setPx(scroller, HEADER_HEIGHT_VAR, height);

      const running = runningAnimations(header);
      if (running.length > 0) {
        // Mid-transition heights must not leak into the reserved space; measure again once the header settles.
        if (!waitingForAnimations.current) {
          waitingForAnimations.current = true;
          Promise.allSettled(running.map((animation) => animation.finished)).then(() => {
            waitingForAnimations.current = false;
            if (headerRef.current) {
              measure(headerRef.current);
            }
          });
        }
        return;
      }

      if (!isCompactRef.current || height > readPx(scroller, HEADER_OFFSET_VAR)) {
        setPx(scroller, HEADER_OFFSET_VAR, height);
      }
    },
    [headerRef, scrollerRef],
  );

  useRafResizeObserver(headerRef, measure, { box: 'border-box', remeasureKey: isCompact });

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    return () => {
      scroller?.style.removeProperty(HEADER_HEIGHT_VAR);
      scroller?.style.removeProperty(HEADER_OFFSET_VAR);
    };
  }, [scrollerRef]);
};
